/**
 * features/compliance/complianceMeta.ts
 * Templates for common Botswana statutory documents and small helpers
 * for renewal status.
 */
import type { ComplianceKind, ComplianceItem } from '@/types';

export interface ComplianceTemplate {
  kind: ComplianceKind;
  label: string;
  authority?: string;
  /** Typical validity in months; used to suggest the expiry date. */
  expiryMonths?: number;
  blurb: string;
}

export const COMPLIANCE_TEMPLATES: Record<ComplianceKind, ComplianceTemplate> = {
  tax_clearance: {
    kind: 'tax_clearance',
    label: 'Tax Clearance Certificate',
    authority: 'BURS',
    expiryMonths: 12,
    blurb: 'Valid 12 months — renew before it expires.',
  },
  incorporation: {
    kind: 'incorporation',
    label: 'Certificate of Incorporation',
    authority: 'CIPA',
    blurb: 'Permanent record; drives your annual-return date.',
  },
  annual_return: {
    kind: 'annual_return',
    label: 'Annual Return',
    authority: 'CIPA',
    expiryMonths: 12,
    blurb: 'Filed yearly with CIPA.',
  },
  vat: {
    kind: 'vat',
    label: 'VAT Registration',
    authority: 'BURS',
    blurb: 'VAT registration certificate.',
  },
  trade_licence: {
    kind: 'trade_licence',
    label: 'Trade Licence',
    authority: 'Local council',
    expiryMonths: 12,
    blurb: 'Renew annually.',
  },
  other: {
    kind: 'other',
    label: 'Other document',
    expiryMonths: 12,
    blurb: 'Any other statutory record.',
  },
};

export const COMPLIANCE_LIST = Object.values(COMPLIANCE_TEMPLATES);

export const KIND_LABEL: Record<ComplianceKind, string> = {
  tax_clearance: 'Tax Clearance',
  incorporation: 'Incorporation',
  annual_return: 'Annual Return',
  vat: 'VAT',
  trade_licence: 'Trade Licence',
  other: 'Other',
};

export type RenewalStatus = 'valid' | 'soon' | 'expired' | 'none';

export function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

export function statusOf(item: Pick<ComplianceItem, 'expiryDate'>, soonDays = 60): RenewalStatus {
  const n = daysUntil(item.expiryDate);
  if (n === null) return 'none';
  if (n < 0) return 'expired';
  if (n <= soonDays) return 'soon';
  return 'valid';
}

export function addMonths(iso: string, months: number): string {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

export function fmtDate(iso?: string): string {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}
